import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { useIntersectionObserver } from '@trqk/hooks';
import { LeftNavHeader } from '@trqk/components';
import { updateAndReplaceURLSearchParams } from '@trqk/utils';

import { WebLayout } from '../../templates';
import {
    useAnchorLinks,
    useStatementSelection,
    useEntities,
    useModal,
} from '../../../hooks';
import { isOnManageStatementsRoute } from '../../../utils';
import {
    StatementsCharts,
    StatementsHeaderContent,
    PerformanceData,
} from './components';
import {
    ShouldSelectStatements,
    ShouldUploadStatements,
} from '../../emptyStates';
import { sectionMap, sectionIds, defaultSectionConfigs } from './constants';
import { preFilterConfigs } from './components/PerformanceData/constants';
import {
    CHILD_PATHS,
    FULL_PATHS,
    HASHES,
    MAIN_CONTENT_ID,
    PATHS,
} from '../../../constants';
import { ManageStatements } from '../../modals';

const nullURLParams = Array.from(preFilterConfigs.values()).map(
    ({ urlParamKey }: PreFilterConfig): URLSearchParam => ({
        key: urlParamKey,
        value: null,
    }),
);

/**
 * Displays a visualization of one or more PRO statements, either as a series of charts or as a data
 * table.
 */
export const Statements = (): JSX.Element => {
    // We're not using react-router-dom's useParams() here because we want to preserve the '/charts'
    // or '/table' section of the path when doing history.replace().
    const isShowingCharts = useRouteMatch({
        path: FULL_PATHS.STATEMENTS_CHARTS,
        strict: true,
        sensitive: true,
    });
    const displayMode = isShowingCharts ? 'charts' : 'table';
    const history = useHistory();

    useEffect(
        () => () => {
            // Delete URL params used by the Performances table filters when this page unmounts
            updateAndReplaceURLSearchParams(history, nullURLParams);
        },
        [history],
    );

    const setDisplayMode = useCallback(
        (nextDisplayMode: StatementsHeaderDisplayMode): void => {
            history.push({
                pathname: `${PATHS.STATEMENTS}/${nextDisplayMode}`,
                search: history.location.search,
            });
        },
        [history.push, history.location.search],
    );

    const modal = useModal();
    const onManageStatementsRoute = isOnManageStatementsRoute(
        history.location.pathname,
    );

    const modalIsOpenRef = useRef(false);
    // Remember the last section ID that was used in the URL hash so it can be restored when the
    // modal is closed
    const [lastSectionId, setLastSectionId] = useState('');

    // When modal closes, go to the previous location and preserve the location.search value
    const handleClose = useCallback(() => {
        const {
            location: { pathname },
        } = history;

        const parentPath = pathname.replace(CHILD_PATHS.STATEMENTS.MANAGE, '');

        history.push({
            search: history.location.search,
            pathname: parentPath,
        });
    }, [history.push, history.location.search, history.location.pathname]);

    // If we are on the `/statements/charts/manage` or `statements/table/manage` route, open the
    // ManageStatements modal; if we are on some other route, close the ManageStatements modal
    useEffect((): void => {
        // If we're on a `/statements/*/manage` route and the modal is not yet open,
        if (onManageStatementsRoute && modalIsOpenRef.current === false) {
            // Keep modalIsOpenRef.current updated
            modalIsOpenRef.current = true;

            // Open modal
            modal.add({
                Content: ManageStatements,
                handleClose,
            });
        }

        // If the modal is closed when on `/statements/charts`, restore the section ID that was in
        // the URL hash before the modal was opened
        if (
            !onManageStatementsRoute &&
            displayMode === 'charts' &&
            modalIsOpenRef.current === true
        ) {
            history.replace({
                hash: lastSectionId,
                search: history.location.search,
            });
        }

        // If we're not on the `/statements/*/manage` route
        if (!onManageStatementsRoute && modalIsOpenRef.current === true) {
            // make sure modalIsOpenRef.current is updated
            modalIsOpenRef.current = false;
        }
    }, [
        displayMode,
        lastSectionId,
        modal,
        modalIsOpenRef.current,
        onManageStatementsRoute,
        handleClose,
        history.replace,
        history.location.search,
    ]);

    const { statementSelectionProps } = useStatementSelection();
    const { selectedIdsString, hasStatements, hasSelectedStatement } =
        statementSelectionProps;

    const [sectionConfigs, setSectionConfigs] = useState<HeaderSectionConfig[]>(
        defaultSectionConfigs,
    );

    const [showMediaExplorer, setShowMediaExplorer] = useState(true);

    const sectionsToRender = sectionConfigs.reduce(
        (
            acc: HeaderSection[],
            sectionConfig: HeaderSectionConfig,
        ): HeaderSection[] => {
            if (sectionConfig.render) {
                if (sectionConfig.section.id === HASHES.STATEMENTS.EXPLORER) {
                    if (showMediaExplorer) {
                        acc.push(sectionConfig.section);
                    }
                } else {
                    acc.push(sectionConfig.section);
                }
            }

            return acc;
        },
        [] as HeaderSection[],
    );

    const displaySections = displayMode === 'charts' ? sectionsToRender : [];

    const { activeScrollObservers } = useAnchorLinks();

    const statementsChartsScrollStatus =
        activeScrollObservers[FULL_PATHS.STATEMENTS_CHARTS];

    const onSetActiveSectionId = useCallback(
        (id: string) => {
            // Remember the current section ID so it can be replaced when closing the Manage
            // Statements modal
            setLastSectionId(id);

            // If the /statements/charts path does not have an active scroll observer, which will
            // scroll a section into view once it exists in the DOM ...
            if (statementsChartsScrollStatus === false) {
                // and:
                // 1) the location.hash does not match the active section id
                // 2) the modal is not open
                // replace the URL hash with the current section ID
                if (history.location.hash !== id && !modalIsOpenRef.current) {
                    history.replace({
                        search: history.location.search,
                        hash: id,
                    });
                }
            }
        },
        [
            statementsChartsScrollStatus,
            history.location.hash,
            history.location.search,
            modalIsOpenRef.current,
        ],
    );

    const { activeSectionId, observe } = useIntersectionObserver({
        defaultSectionId:
            displaySections?.[0]?.id ?? sectionMap.earningsAndRanges.id,
        sectionIds,
        observerOptions: {
            root: document.querySelector(MAIN_CONTENT_ID),
            rootMargin: '0px',
            threshold: 0.8,
        },
        onSetActiveSectionId,
    });

    const { noSelectedEntity } = useEntities();
    const shouldUploadStatements = noSelectedEntity && !hasStatements;
    const shouldSelectStatements = !noSelectedEntity && !hasSelectedStatement;

    return (
        <WebLayout
            collapsedHeaderPadding={displayMode === 'charts' ? 'sm' : undefined}
            headerPadding={displayMode === 'charts' ? 'lg' : 'sm'}
            mainWidth={displayMode === 'charts' ? undefined : null}
            header={{
                content: (
                    <LeftNavHeader
                        activeSectionId={activeSectionId}
                        sections={displaySections}
                    >
                        <StatementsHeaderContent
                            displayMode={displayMode}
                            setDisplayMode={setDisplayMode}
                            statementSelectionProps={statementSelectionProps}
                        />
                    </LeftNavHeader>
                ),
                wrapperClass: 'semi-transparent-header',
            }}
        >
            {hasSelectedStatement && displayMode === 'charts' && (
                <StatementsCharts
                    ref={observe}
                    selectedIdsString={selectedIdsString}
                    setSectionConfigs={setSectionConfigs}
                    setShowMediaExplorer={setShowMediaExplorer}
                />
            )}
            {hasSelectedStatement && displayMode === 'table' && (
                <PerformanceData selectedIdsString={selectedIdsString} />
            )}
            {shouldUploadStatements && <ShouldUploadStatements />}
            {shouldSelectStatements && <ShouldSelectStatements />}
        </WebLayout>
    );
};
